import { Grid, useTheme } from "@mui/material";
import { ReactComponent as CheckIcon } from "Components/_Shared/Icons/Assets/check.svg";
import { ReactComponent as StackIcon } from "Components/_Shared/Icons/Assets/stack.svg";
import { ReactComponent as UnavailableIcon } from "Components/_Shared/Icons/Assets/unavailable.svg";
import { ReactComponent as UserIcon } from "Components/_Shared/Icons/Assets/user-male.svg";
import { Tile } from "Components/_Shared/Tile";
import { useGetAnalysisClients } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { FinancialAssetItem } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { FINANCIAL_SUBTYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { formatClientName } from "Infrastructure/Utils/ClientUtils";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { type FC, useContext } from "react";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { Tag } from "../_Shared/Tag";

type Props = {
  readonly asset: FinancialAssetItem;
  readonly onEdit: () => void;
};

export const FinancialAssetTile: FC<Props> = ({ asset, onEdit }) => {
  const { versionId, analysisId } = useContext(AnalysisDetailContext);
  const { t } = useResource();
  const theme = useTheme();

  const { data: clientsData } = useGetAnalysisClients(analysisId, versionId);

  const client = clientsData?.clients.find((client) => client.guid === asset.clientGuid);
  return (
    <Tile title={asset.name} subtitle={asset.note ?? undefined} onClick={onEdit}>
      <Grid container gap={Spacing.M}>
        <Tag
          variant="solid"
          color="grey"
          title={t(Resources.AnalysisDetail.Assets.Financial.FinancialAssetType[FINANCIAL_SUBTYPE_RESOURCE_KEY[asset.financialSubType]])}
        />
        <Tag variant="solid" color="grey" icon={<StackIcon />} title={formatCurrency(asset.amount.value, asset.amount.currencyCode)} />
        {client && (
          <Tag
            variant="solid"
            color="info"
            title={formatClientName(client.firstName, client.surname, null)}
            icon={<UserIcon width={12} height={12} />}
          />
        )}
        <Tag
          variant="solid"
          color={asset.isUsableForGoals ? "success" : "error"}
          icon={
            asset.isUsableForGoals ? (
              <CheckIcon width={12} height={12} color={theme.colors.successPrimary} />
            ) : (
              <UnavailableIcon width={12} height={12} color={theme.colors.dangerPrimary} />
            )
          }
          title={t(asset.isUsableForGoals ? Resources.AnalysisDetail.Assets.Usable : Resources.AnalysisDetail.Assets.Unusable)}
        />
      </Grid>
    </Tile>
  );
};
